import React, { useMemo } from 'react'

import { Icon } from '@components/icon.component'
import { ActionIcon } from '@components/mantine/action-icon.component'
import { MultiSelect } from '@components/mantine/multi-select.component'
import { Select } from '@components/mantine/select.component'
import { FieldHeaderContent } from '@src/pages/workflow/client/fields/field-header-content.component'
import { AssistedResponseModal } from '@src/pages/workflow/client/workflow-refactor/assisted-response-modal.component'
import { modalService } from '@utils/modal.service'
import { Control, useController } from 'react-hook-form'

type WorkflowSelectProps = {
  field: Field
  isRequired?: boolean
  section: ActivitySection
  control: Control
  readOnly?: boolean
}

export function WorkflowSelect({
  field,
  isRequired,
  section,
  control,
  readOnly,
}: WorkflowSelectProps) {
  const {
    field: { onChange, value, disabled, ..._fieldProps },
    fieldState: { error },
  } = useController({
    disabled: readOnly,
    name: `${field.version}_${field.id}`,
    control,
    rules: {
      required: isRequired && 'This field is required',
    },
  })

  function createMarkup() {
    return { __html: field.description }
  }

  function handleSelectMulti(selectedIds: string[]) {
    if (disabled) return
    onChange(selectedIds)
  }

  function handleSelectSingle(selection: { id: string } | null) {
    if (disabled) return
    onChange(selection ? [selection.id] : [])
  }

  const selectData = useMemo(
    () => field.choices.map((c: any) => ({ value: c.id, label: c.label })),
    [field.choices],
  )

  if (!field.choices.filter(({ label }: { label: string }) => label).length) {
    return (
      <div className="flex flex-col">
        <div className="field-label">
          {field.label}
          {isRequired && <span className="aria-invisible"> Required</span>}
        </div>
        <div
          className="field-description"
          dangerouslySetInnerHTML={createMarkup()}
        />
        <div className="text-danger flex items-center">
          <Icon
            name="circle-exclamation"
            className="mr-2"
          />{' '}
          No Select Box choices have been added. This field and message will not
          display to users.
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <FieldHeaderContent
        field={field}
        isRequired={isRequired}
        section={section}
        actions={
          <>
            <ActionIcon
              size="sm"
              onClick={() => {
                modalService
                  .render(AssistedResponseModal, { field, section })
                  .then(({ response }: { response: string }) => {
                    const responses = field.choices.reduce(
                      (acc: string[], { id, label }: { id: string; label: string }) => {
                        const has = response.includes(label)
                        if (has) return [...acc, id]
                        return acc
                      },
                      [],
                    )
                    onChange(responses)
                  })
              }}
              icon="wand-magic-sparkles"
            />
          </>
        }
      />
      {field.multiselect ? (
        <>
          <MultiSelect
            data={selectData}
            value={value}
            onChange={handleSelectMulti}
            fullWidth
          />
          {error && (
            <div className="mt-1 text-xl text-danger">{error.message}</div>
          )}
        </>
      ) : (
        <>
          <Select
            clearable
            fullWidth
            data={selectData}
            value={field.choices.find(({ id }: { id: string }) => value.includes(id))?.id}
            onChange={(val: string | null) => {
              const selection = val
                ? field.choices.find((c: any) => c.id === val)
                : null
              handleSelectSingle(selection)
            }}
          />
          {error && (
            <div className="mt-1 text-xl text-danger">{error.message}</div>
          )}
        </>
      )}
    </div>
  )
}
